import { Fragment, useState } from 'react';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../stores';
import { ICampaignDomainState } from '../../models/bidder-domains.model';
import { DispatchLogsTypes } from '@monorepo/controlled/src/hooks/use-logs';
import { BidderDomainsApi } from '../../apis/bidder-domains.api';
import { SecondaryText } from '@monorepo/base/src/components/buttons/buttons';
import { SelectedLinerButton } from '@monorepo/base/src/components/table/action-liner/liner-buttons';
import { ITableActions } from './default.actions';
import { IDebugProps } from '@monorepo/tools/src/lib/interfaces/debug';
import { suffixToDataAttr } from '@monorepo/tools/src/lib/models/data-attr.model';

interface IBidderDomainsStatesActions {
	campaignId?: number;
	debugProps?: IDebugProps;
	onRefresh?: () => void;
}

export const useBidderDomainsStatesActions = (props: IBidderDomainsStatesActions = {}) => {
	const { campaignId, debugProps, onRefresh } = props;
	const { toastsStore } = useStores<IAdminxStore>();
	const { dataAttrs } = debugProps || {};

	const [isActionOngoing, setIsActionOngoing] = useState<boolean>(false);

	const removeFromCache = async (domains: ICampaignDomainState[]) => {
		if (!campaignId) {
			toastsStore.addToast({
				msg: 'Campaign ID is required',
				type: DispatchLogsTypes.Error,
			});
			return;
		}

		setIsActionOngoing(true);
		try {
			const domainNames = domains.map(domain => domain.domain);
			const response = await BidderDomainsApi.deleteDomainCampaignCache({
				campaign_id: campaignId,
				domains: domainNames,
			});

			if (response.domain_errors && response.domain_errors.length > 0) {
				const errorDomains = response.domain_errors.map(err => err.domain).join(', ');
				const successDomains = domainNames.filter(domain => !response.domain_errors?.some(err => err.domain === domain));

				if (successDomains.length > 0) {
					toastsStore.addToast({
						msg:
							successDomains.length === 1
								? `Domain ${successDomains[0]} removed from cache successfully`
								: `Domains ${successDomains.join(', ')} removed from cache successfully`,
						type: DispatchLogsTypes.Success,
					});
				}

				toastsStore.addToast({
					msg: `Failed to remove domains ${errorDomains} from cache`,
					type: DispatchLogsTypes.Error,
				});
			} else {
				toastsStore.addToast({
					msg:
						domainNames.length === 1
							? `Domain ${domainNames[0]} removed from cache successfully`
							: `${domainNames.length} domain(s) removed from cache successfully`,
					type: DispatchLogsTypes.Success,
				});
			}

			if (onRefresh) {
				onRefresh();
			}
		} catch (error) {
			console.error('Error removing domains from cache:', error);
			toastsStore.addToast({
				msg: `Failed to remove domains from cache: ${error instanceof Error ? error.message : 'Unknown error'}`,
				type: DispatchLogsTypes.Error,
			});
		} finally {
			setIsActionOngoing(false);
		}
	};

	const removeFromTrash = async (domains: ICampaignDomainState[]) => {
		if (!campaignId) {
			toastsStore.addToast({
				msg: 'Campaign ID is required',
				type: DispatchLogsTypes.Error,
			});
			return;
		}

		setIsActionOngoing(true);
		try {
			const domainNames = domains.map(domain => domain.domain);
			const response = await BidderDomainsApi.openClosedDomain({
				campaign_id: campaignId,
				domains: domainNames,
			});

			if (response.domain_errors && response.domain_errors.length > 0) {
				const errorDomains = response.domain_errors.map(err => err.domain).join(', ');
				const successDomains = domainNames.filter(domain => !response.domain_errors?.some(err => err.domain === domain));

				if (successDomains.length > 0) {
					toastsStore.addToast({
						msg:
							successDomains.length === 1
								? `Domain ${successDomains[0]} removed from trash successfully`
								: `Domains ${successDomains.join(', ')} removed from trash successfully`,
						type: DispatchLogsTypes.Success,
					});
				}

				toastsStore.addToast({
					msg: `Failed to remove domains ${errorDomains} from trash`,
					type: DispatchLogsTypes.Error,
				});
			} else {
				toastsStore.addToast({
					msg:
						domainNames.length === 1
							? `Domain ${domainNames[0]} removed from trash successfully`
							: `${domainNames.length} domain(s) removed from trash successfully`,
					type: DispatchLogsTypes.Success,
				});
			}

			if (onRefresh) {
				onRefresh();
			}
		} catch (error) {
			console.error('Error removing domains from trash:', error);
			toastsStore.addToast({
				msg: `Failed to remove domains from trash: ${error instanceof Error ? error.message : 'Unknown error'}`,
				type: DispatchLogsTypes.Error,
			});
		} finally {
			setIsActionOngoing(false);
		}
	};

	const SelectedActions = (props: ITableActions<ICampaignDomainState>) => {
		const { rows } = props;
		const domains = rows.map(row => row.original);

		if (!campaignId || domains.length === 0) {
			return null;
		}

		const domainsAnalysis = domains.reduce(
			(acc, domain) => {
				const isTrash = domain.status === 'TRASH';
				const isPaused = domain.paused;

				if (isTrash) {
					acc.trashDomains.push(domain);
				} else {
					acc.cacheDomains.push(domain);
					if (isPaused) {
						acc.pausedCacheDomains.push(domain);
					} else {
						acc.notPausedCacheDomains.push(domain);
					}
				}

				return acc;
			},
			{
				trashDomains: [] as ICampaignDomainState[],
				cacheDomains: [] as ICampaignDomainState[],
				pausedCacheDomains: [] as ICampaignDomainState[],
				notPausedCacheDomains: [] as ICampaignDomainState[],
			}
		);

		const canRemoveFromCache =
			domainsAnalysis.trashDomains.length === 0 &&
			domainsAnalysis.pausedCacheDomains.length > 0 &&
			domainsAnalysis.notPausedCacheDomains.length === 0;

		const canRemoveFromTrash = domainsAnalysis.cacheDomains.length === 0 && domainsAnalysis.trashDomains.length > 0;

		return (
			<Fragment>
				<SelectedLinerButton>
					<SecondaryText
						icon={'chart-breakout-circle'}
						onClick={() => removeFromCache(domains)}
						textColor={'white'}
						disabled={!canRemoveFromCache || isActionOngoing}
						debugProps={{ dataAttrs: suffixToDataAttr('_selected_remove_cache', dataAttrs) }}>
						{isActionOngoing ? 'Removing...' : 'Remove from Cache'}
					</SecondaryText>
				</SelectedLinerButton>
				<SelectedLinerButton>
					<SecondaryText
						icon={'chart-breakout-square'}
						onClick={() => removeFromTrash(domains)}
						textColor={'white'}
						disabled={!canRemoveFromTrash || isActionOngoing}
						debugProps={{ dataAttrs: suffixToDataAttr('_selected_remove_trash', dataAttrs) }}>
						{isActionOngoing ? 'Removing...' : 'Remove from Trash'}
					</SecondaryText>
				</SelectedLinerButton>
			</Fragment>
		);
	};

	return {
		removeFromCache,
		removeFromTrash,
		SelectedActions,
		isActionOngoing,
	};
};
